import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Printer, CheckCircle2, Download } from "lucide-react";
import { downloadPdf } from "@/components/SupplierOrder";
import { api, errMsg, fmtEuro, quoteFileName } from "@/lib/api";
import { useLookups } from "@/lib/useLookups";
import { PageHeader, Panel, Field, GoldButton, GhostButton, Select, Empty, inputCls } from "@/components/common";
import { TYPE_LABEL, QuoteStatus } from "@/pages/Quotes";

const blankQ = (type, client) => ({ client_id: client || "", type: type || "laser", title: "", items: [], discount_percent: 0, valid_until: "", notes: "", status: "bozza" });

const ItemRow = ({ it, i, update, remove, locked }) => (
  <li className="grid grid-cols-12 items-center gap-2 py-2" data-testid={`quote-item-${i}`}>
    <input className={`${inputCls} col-span-12 sm:col-span-6`} value={it.description} disabled={locked} onChange={(e) => update({ description: e.target.value })} data-testid={`quote-item-desc-${i}`} />
    <input type="number" min="1" className={`${inputCls} col-span-4 sm:col-span-2`} value={it.sessions} disabled={locked} onChange={(e) => update({ sessions: e.target.value })} data-testid={`quote-item-sessions-${i}`} />
    <input type="number" min="0" step="0.5" className={`${inputCls} col-span-4 sm:col-span-2`} value={it.unit_price} disabled={locked} onChange={(e) => update({ unit_price: e.target.value })} data-testid={`quote-item-price-${i}`} />
    <div className="col-span-3 sm:col-span-1 text-right text-sm font-medium">{fmtEuro((Number(it.unit_price) || 0) * (Number(it.sessions) || 0))}</div>
    {!locked && <button onClick={remove} className="col-span-1 justify-self-end text-[#6E675F] hover:text-red-700" data-testid={`quote-item-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
  </li>
);

const AddItems = ({ q, zones, treatments, addItem }) => {
  const [tid, setTid] = useState("");
  if (q.type === "laser") {
    return zones.length === 0
      ? <p className="text-sm text-[#9E958A]">Nessuna zona laser. <Link to="/preventivi" className="text-[#C5A059]">Aggiungile nella scheda "Zone laser".</Link></p>
      : <div className="flex flex-wrap gap-2">{zones.map((z) => (
          <button key={z.id} onClick={() => addItem({ description: z.name, unit_price: z.price, sessions: z.sessions || 6, ref_id: z.id })} data-testid={`add-zone-${z.id}`}
            className="rounded-full border border-[#E8E1D7] bg-white px-3.5 py-1.5 text-sm hover:border-[#C5A059] transition-colors">+ {z.name} <span className="text-[#9E958A]">{fmtEuro(z.price)}</span></button>
        ))}</div>;
  }
  const pick = () => {
    const t = treatments.find((x) => x.id === tid);
    if (t) addItem({ description: t.name, unit_price: t.price, sessions: 1, ref_id: t.id });
    setTid("");
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Select value={tid} onChange={(e) => setTid(e.target.value)} placeholder="Scegli dal listino trattamenti" options={treatments.map((t) => ({ value: t.id, label: `${t.name} · ${fmtEuro(t.price)}` }))} className="max-w-sm bg-white" data-testid="quote-treatment-select" />
      <GhostButton onClick={pick} disabled={!tid} data-testid="quote-add-treatment-button"><Plus className="h-4 w-4" /> Aggiungi</GhostButton>
      <GhostButton onClick={() => addItem({ description: "Voce personalizzata", unit_price: 0, sessions: 1, ref_id: "" })} data-testid="quote-add-custom-button">Voce libera</GhostButton>
    </div>
  );
};

export default function QuoteEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const lk = useLookups(["clients", "treatments", "laser_zones"]);
  const [q, setQ] = useState(id ? null : blankQ(sp.get("type"), sp.get("client")));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (id) { api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch(() => setQ(false)); } }, [id]);
  if (q === false) return <div className="text-[#6E675F]">Preventivo non trovato.</div>;
  if (!q) return null;

  const locked = q.status === "accettato";
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  const items = q.items;
  const subtotal = items.reduce((s, i) => s + (Number(i.unit_price) || 0) * (Number(i.sessions) || 0), 0);
  const disc = subtotal * (Number(q.discount_percent) || 0) / 100;

  const body = () => ({ ...Object.fromEntries(Object.keys(blankQ()).map((k) => [k, q[k]])), discount_percent: Number(q.discount_percent) || 0,
    items: items.map((i) => ({ description: i.description, unit_price: Number(i.unit_price) || 0, sessions: Math.max(1, Number(i.sessions) || 1), ref_id: i.ref_id || "" })) });
  const save = async () => {
    if (!q.client_id) return toast.error("Seleziona il cliente");
    if (!items.length) return toast.error("Aggiungi almeno una voce");
    setSaving(true);
    try {
      const { data } = id ? await api.put(`/quotes/${id}`, body()) : await api.post("/quotes", body());
      toast.success("Preventivo salvato");
      setQ(data);
      if (!id) nav(`/preventivi/${data.id}`, { replace: true });
      return data;
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const accept = async () => {
    if (!window.confirm("Confermi che il cliente ha accettato? Le sedute verranno aggiunte ai trattamenti da effettuare.")) return;
    try {
      if (!(await save())) return;
      const { data } = await api.post(`/quotes/${id}/accept`);
      setQ(data);
      toast.success(`Preventivo accettato: ${data.created_treatments} sedute aggiunte alla scheda cliente`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { if (window.confirm(q.status === "accettato" ? "Eliminare il preventivo? Verranno eliminate anche le sedute ancora da effettuare create da questo preventivo." : "Eliminare il preventivo?")) { await api.delete(`/quotes/${id}`); nav("/preventivi"); } };

  return (
    <div data-testid="quote-editor-page">
      <Link to="/preventivi" className="mb-4 inline-flex items-center gap-1 text-sm text-[#6E675F] hover:text-[#C5A059]" data-testid="back-to-quotes"><ArrowLeft className="h-4 w-4" /> Preventivi</Link>
      <PageHeader overline={q.number || "Nuovo preventivo"} title={TYPE_LABEL[q.type]}>
        {id && <QuoteStatus s={q.status} />}
        {id && <GhostButton onClick={() => window.open(`/preventivi/${id}/stampa`, "_blank")} data-testid="quote-print-button"><Printer className="h-4 w-4" /> Anteprima</GhostButton>}
        {id && <GhostButton onClick={() => downloadPdf(`/quotes/${id}/pdf`, quoteFileName(q))} data-testid="quote-editor-download-pdf"><Download className="h-4 w-4" /> Scarica PDF</GhostButton>}
        {id && !locked && <GhostButton onClick={accept} className="text-[#2D6A4F]" data-testid="quote-accept-button"><CheckCircle2 className="h-4 w-4" /> Accettato</GhostButton>}
        {id && <GhostButton onClick={del} className="text-red-700" data-testid="quote-delete-button"><Trash2 className="h-4 w-4" /></GhostButton>}
      </PageHeader>
      {locked && <div className="mb-6 rounded-xl border border-[#2D6A4F]/20 bg-[#2D6A4F]/5 px-4 py-3 text-sm text-[#2D6A4F]" data-testid="quote-locked-note">Preventivo accettato: le sedute sono state aggiunte ai trattamenti da effettuare del cliente.</div>}

      <Panel className="p-6 mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="lg:col-span-2"><Field label="Cliente *"><Select value={q.client_id} onChange={set("client_id")} disabled={locked} placeholder="Seleziona cliente" options={lk.clients.map((c) => ({ value: c.id, label: `${c.last_name} ${c.first_name}` }))} data-testid="quote-client-select" /></Field></div>
        <Field label="Tipo">
          <Select value={q.type} onChange={(e) => setQ({ ...q, type: e.target.value, items: [] })} disabled={locked || !!id} options={Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }))} data-testid="quote-type-select" />
        </Field>
        <Field label="Stato">
          <Select value={q.status} onChange={set("status")} disabled={locked} options={(locked ? ["accettato"] : ["bozza", "inviato", "rifiutato"]).map((s) => ({ value: s, label: { bozza: "Bozza", inviato: "Inviato", rifiutato: "Rifiutato", accettato: "Accettato" }[s] }))} data-testid="quote-status-select" />
        </Field>
        <div className="lg:col-span-2"><Field label="Titolo"><input className={inputCls} value={q.title} disabled={locked} onChange={set("title")} placeholder="Es. Percorso gambe e ascelle" data-testid="quote-title-input" /></Field></div>
        <Field label="Valido fino al"><input type="date" className={inputCls} value={q.valid_until} disabled={locked} onChange={set("valid_until")} data-testid="quote-valid-input" /></Field>
      </Panel>

      <Panel className="p-6 mb-6">
        <h3 className="font-serif text-2xl mb-4">{q.type === "laser" ? "Zone da trattare" : "Trattamenti del pacchetto"}</h3>
        {!locked && <div className="mb-5"><AddItems q={q} zones={lk.laser_zones || []} treatments={lk.treatments} addItem={(it) => setQ({ ...q, items: [...items, it] })} /></div>}
        <div className="hidden sm:grid grid-cols-12 gap-2 text-xs uppercase tracking-[0.14em] text-[#6E675F] border-b border-[#E8E1D7] pb-2">
          <span className="col-span-6">Descrizione</span><span className="col-span-2">Sedute</span><span className="col-span-2">€ a seduta</span><span className="col-span-1 text-right">Totale</span>
        </div>
        {items.length === 0 ? <div className="mt-3"><Empty testId="quote-items-empty">Nessuna voce aggiunta.</Empty></div> : (
          <ul className="divide-y divide-[#E8E1D7]">
            {items.map((it, i) => <ItemRow key={i} it={it} i={i} locked={locked} update={(p) => setQ({ ...q, items: items.map((x, j) => (j === i ? { ...x, ...p } : x)) })} remove={() => setQ({ ...q, items: items.filter((_, j) => j !== i) })} />)}
          </ul>
        )}
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel className="p-6"><Field label="Note per il cliente"><textarea rows={4} className={inputCls} value={q.notes} disabled={locked} onChange={set("notes")} data-testid="quote-notes-input" /></Field></Panel>
        <Panel className="p-6 space-y-3">
          <div className="flex justify-between text-sm"><span className="text-[#6E675F]">Subtotale</span><span data-testid="quote-subtotal">{fmtEuro(subtotal)}</span></div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-[#6E675F]">Sconto %</span>
            <input type="number" min="0" max="100" className={`${inputCls} !w-24 text-right`} value={q.discount_percent} disabled={locked} onChange={set("discount_percent")} data-testid="quote-discount-input" />
          </div>
          {disc > 0 && <div className="flex justify-between text-sm text-[#8A6A22]"><span>Sconto</span><span>− {fmtEuro(disc)}</span></div>}
          <div className="flex justify-between border-t border-[#E8E1D7] pt-3"><span className="font-serif text-2xl">Totale</span><span className="font-serif text-3xl" data-testid="quote-total">{fmtEuro(subtotal - disc)}</span></div>
          {!locked && <GoldButton onClick={save} disabled={saving} className="w-full mt-2" data-testid="quote-save-button">{saving ? "Salvataggio…" : "Salva preventivo"}</GoldButton>}
        </Panel>
      </div>
    </div>
  );
}
